/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-restricted-globals */
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Toolbar,
  Tooltip,
  Typography,
  TableSortLabel,
  TableBody
} from '@mui/material';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import React, { useState } from 'react';
import { showToast } from 'modules/layout/layout.actions';
import { useDropzone } from 'react-dropzone';
import { Delete, Download } from '@mui/icons-material';
import { postAuthorizationComment, updateAuthorizationComment } from 'modules/authorizations/store/actions/authorizations.actions';
import auth from 'auth/auth';
import FilterListIcon from '@mui/icons-material/FilterList';
import { REACT_APP_INTAKE_API_V2_URL } from 'modules/authorizations/store/actions/attachments.actions';
import SpinnerComponent from '../spinner.component';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const AttachmentsUpload = props => {
  const {
    fileAttachments,
    saveFiles,
    isUploading,
    deleteFile,
    updateFile,
    webViewableText,
    descriptionText,
    displayWebViewable = true,
    displayDescription = true,
    isReadOnly = false,
  } = props;

  const [open, setOpen] = useState(false);
  const [attachments, setAttachments] = useState(fileAttachments);
  const [temporaryAttachments, setTemporaryAttachments] = useState([]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const resolvedAttachments = Array.isArray(fileAttachments) ? fileAttachments : [];

  const headCells = [
    {
      id: 'fileName',
      numeric: false,
      disablePadding: false,
      label: 'File Name',
    },
    {
      id: 'fileExtension',
      numeric: true,
      disablePadding: false,
      label: 'File Type',
    },
    {
      id: 'isWebViewable',
      numeric: true,
      disablePadding: false,
      label: webViewableText,
      display: displayWebViewable
    },
    {
      id: 'uploadedOn',
      numeric: true,
      disablePadding: false,
      label: 'Uploaded On',
    },
    {
      id: 'description',
      numeric: true,
      disablePadding: false,
      label: descriptionText,
      display: displayDescription
    }
  ];

  const handleClose = () => {
    setTemporaryAttachments([]);
    setOpen(false);
  };

  const onDrop = acceptedFiles => {
    setTemporaryAttachments([...temporaryAttachments, ...acceptedFiles]);
  };

  const { getRootProps, getInputProps } = useDropzone({ onDrop });

  const handleSaveFile = async () => {
    setAttachments([...attachments, ...temporaryAttachments]);

    await saveFiles(temporaryAttachments);

    handleClose();
  };

  const getDownloadFileName = (response, attachment) => {
    const contentDisposition = response.headers.get('content-disposition') || '';
    const utf8FileNameMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8FileNameMatch && utf8FileNameMatch[1]) {
      return decodeURIComponent(utf8FileNameMatch[1]);
    }

    const fileNameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
    if (fileNameMatch && fileNameMatch[1]) {
      return fileNameMatch[1];
    }

    return attachment?.fileName || `attachment-${attachment?.attachmentId}`;
  };

  const handleDownloadAttachment = async (attachment) => {
    try {
      const token = auth.getAccessToken();
      if (!token) {
        props.showToast('No authentication token found. Please sign in again.', { severity: 'error' });
        return;
      }

      const response = await fetch(`${REACT_APP_INTAKE_API_V2_URL}api/files/${attachment.attachmentId}/download`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`Download failed with status ${response.status}`);
      }

      const fileBlob = await response.blob();
      const objectUrl = window.URL.createObjectURL(fileBlob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = getDownloadFileName(response, attachment);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error('Attachment download failed:', error);
      props.showToast('Unable to download attachment.', { severity: 'error' });
    }
  };

  const handleRemoveAttachment = async (attachment) => {
    if (!confirm(`Are you sure you want to delete the attachment?`)) {
        return;
    }
    
    await deleteFile(attachment);
  };

  const handleWebViewable = async (isChecked, attachment) => {
    // attachment.isWebViewable = isChecked;

    await updateFile(attachment);
  }

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 && resolvedAttachments ? Math.max(0, (1 + page) * rowsPerPage - resolvedAttachments.length) : 0;
  const pagedAttachments = resolvedAttachments.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const visibleHeadCellCount = headCells.filter(headCell => headCell.display !== false).length;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatFileType = value => {
    if (value == null) return '';
    const fileType = String(value);
    return fileType.length > 20 ? `${fileType.slice(0, 15)}...` : fileType;
  };

  return (
    <Box>
        <Box>
          <Paper sx={{ width: '100%', mb: 2 }}>
                <Toolbar
                    sx={[
                        {
                        pl: { sm: 2 },
                        pr: { xs: 1, sm: 1 },
                        },
                    ]}
                >
                  <FontAwesomeIcon icon={['fas', 'paperclip']} size="2x" />
                        <Typography
                            sx={{ 
                                flex: '1 1 100%',
                                fontSize: '1.3125rem',
                                fontWeight: 'bold',
                                marginLeft: '12px'
                            }}
                            variant="h5"
                            id="tableTitle"
                            component="div"
                        >
                            Attachments
                        </Typography>

                        <Tooltip title="Filter list">
                            <IconButton>
                                <FilterListIcon />
                            </IconButton>
                        </Tooltip>
                </Toolbar>
        
                <Box sx={{ paddingLeft: '16px' }}>
                    <Button color="primary" onClick={() => setOpen(true)} variant="contained" disabled={isReadOnly}>
                        Add Attachments
                    </Button>
                </Box>
        
                <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
                    <Table
                    sx={{ width: '100%', minWidth: 750, tableLayout: 'fixed' }}
                        aria-labelledby="tableTitle"
                        size={'medium'}
                    >
                        <TableHead>
                            <TableRow>
                                {headCells.map((headCell) => {
                                  if(headCell.display === false) {
                                    return null;
                                  }
                                  else {
                                    return (
                                    <TableCell
                                        key={headCell.id}
                                        align={'left'}
                                        padding={'normal'}
                                        //sortDirection={orderBy === headCell.id ? order : false}
                                    >
                                        <TableSortLabel
                                            //active={orderBy === headCell.id}
                                            //direction={orderBy === headCell.id ? order : 'asc'}
                                            //onClick={createSortHandler(headCell.id)}
                                        >
                                            {headCell.label}
                                            {/* {orderBy === headCell.id ? (
                                                <Box component="span" sx={visuallyHidden}>
                                                {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                                </Box>
                                            ) : null} */}
                                        </TableSortLabel>
                                    </TableCell>
                                    )
                                  }
                                    
                                })}
                                <TableCell padding="checkbox">
                                
                                </TableCell>
                                <TableCell padding="checkbox">
                                
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {pagedAttachments.map((row) => {
                              if(!displayWebViewable && !displayDescription) {
                                  return (
                                    <TableRow key={row.attachmentId || row.fileName}>
                                        <TableCell align="left">{row.fileName}</TableCell>
                                      <TableCell align="left">{formatFileType(row.fileExtension)}</TableCell>
                                        <TableCell align="left">{row.uploadedOn ? new Date(row.uploadedOn).toLocaleDateString('en-US') : ''}</TableCell>
                                        <TableCell align="left">
                                            <Tooltip arrow placement="right" title="Download">
                                                <IconButton color="primary" onClick={() => handleDownloadAttachment(row)}>
                                                    <Download />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell align="left">
                                            <Tooltip arrow placement="right" title="Delete">
                                            <IconButton color="error" onClick={() => handleRemoveAttachment(row)} disabled={isReadOnly}>
                                                    <Delete />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                );
                              }
                              else {
                                return (
                                    <TableRow key={row.attachmentId || row.fileName}>
                                        <TableCell align="left">{row.fileName}</TableCell>
                                      <TableCell align="left">{formatFileType(row.fileExtension)}</TableCell>
                                        <TableCell align="left">
                                            <Checkbox
                                                checked={row.isWebViewable}
                                              disabled={isReadOnly}
                                                onChange={(e) => handleWebViewable(e.target.checked, row)}
                                            />
                                        </TableCell>
                                        <TableCell align="left">{row.uploadedOn ? new Date(row.uploadedOn).toLocaleDateString('en-US') : ''}</TableCell>
                                        <TableCell align="left">{row.description}</TableCell>
                                        <TableCell align="left">
                                            <Tooltip arrow placement="right" title="Download">
                                                <IconButton color="primary" onClick={() => handleDownloadAttachment(row)}>
                                                    <Download />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell align="left">
                                            <Tooltip arrow placement="right" title="Delete">
                                              <IconButton color="error" onClick={() => handleRemoveAttachment(row)} disabled={isReadOnly}>
                                                    <Delete />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                );
                              }
                                
                            })}
                              {pagedAttachments.length === 0 && (
                                <TableRow>
                                  <TableCell colSpan={visibleHeadCellCount + 2} align="center" sx={{ color: 'text.secondary' }}>
                                    No attachments
                                  </TableCell>
                                </TableRow>
                              )}
                            {emptyRows > 0 && (
                                <TableRow
                                style={{
                                    height: (53) * emptyRows,
                                }}
                                >
                                    <TableCell colSpan={6} />
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                  count={resolvedAttachments.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>
        </Box>
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
          <DialogTitle>Upload Attachments</DialogTitle>
          <DialogContent>
            <Box
              {...getRootProps()}
              sx={{
                border: '2px dashed gray',
                borderRadius: '8px',
                padding: '16px',
                textAlign: 'center',
                cursor: 'pointer',
                marginBottom: '16px'
              }}
            >
              <input {...getInputProps()} />
              <Typography>Drag & drop files here, or click to select files</Typography>
            </Box>
            <Box>
              {temporaryAttachments.length > 0 ? (
                temporaryAttachments.map((file, index) => <Typography key={index}>{file.name}</Typography>)
              ) : (
                <Typography>No files uploaded</Typography>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            {isUploading && (
              <SpinnerComponent/>
            )}
            {!isUploading && (
              <Box>
                <Button
                  onClick={handleClose}
                  color="secondary"
                  variant="text"
                  sx={{
                    color: '#868686',
                    '&:hover': {
                      color: '#474747',
                      backgroundColor: 'transparent'
                    }
                  }}
                >
                  Cancel
                </Button>
                <Button onClick={handleSaveFile} color="primary">
                  Save Files
                </Button>
              </Box>
            )}
          </DialogActions>
        </Dialog>
    </Box>
  );
};

const mapStateToProps = state => ({});

export default compose(connect(mapStateToProps, { postAuthorizationComment, updateAuthorizationComment, showToast }))(
  AttachmentsUpload
);
